import { useCallback } from 'react';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { WareHouseDto } from 'src/dtos/warehouse/wareHouseDto';
import { WorkPlaceDto } from 'src/dtos/workplace/workPlaceDto';
import useUiuxStore from 'src/store/useUiuxStore';

export type WarehouseProcessType = 'inWarehouse' | 'outWarehouse' | 'badWarehouse' | 'WMS1030_R01' | 'WMS1031_R01';

export type WarehouseValidateMode = 'workplace' | 'WMS1030_R01' | 'WMS1031_R01';

export type ValidateWorkplaceWarehouseParams = {
	mode?: WarehouseValidateMode;
	workPlaceList?: WorkPlaceDto[];
	warehouseCdList: WareHouseDto[];
	wmsCdList: SysCodeDto[];
	workplaceCd?: string;
	logicCenter?: string;
};

export type WorkplaceWarehouseValidateSuccess =
	| {
		valid: true;
		mode: 'workplace';
		logicCenter: string;
		inWarehouseCd: WareHouseDto[];
		outWarehouseCd: WareHouseDto[];
		badWarehouseCd: WareHouseDto[];
	}
	| {
		valid: true;
		mode: 'WMS1030_R01' | 'WMS1031_R01';
		logicCenter: string;
		warehouseCdListToUse: WareHouseDto[];
	};

export type WorkplaceWarehouseValidateResult =
	| { valid: false }
	| WorkplaceWarehouseValidateSuccess;

export const useWarehouseValidate = () => {
	const { setConfirmModalVisible, setConfirmModalProps } = useUiuxStore();

	const warningWorkplaceNullInfo = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			const closeModal = () => {
				setConfirmModalVisible(false);
				setConfirmModalProps(undefined);
			};

			setConfirmModalProps({
				title: '설정 오류',
				message: '공정 정보 등록 화면에서 공정 정보를 하나 이상 등록해주세요',
				confirmText: '확인',
				onConfirm: () => { closeModal(); resolve(true); },
			});
			setConfirmModalVisible(true);
		});
	}, [setConfirmModalProps, setConfirmModalVisible]);

	const warningWarehouseInfo = useCallback((processType: WarehouseProcessType) => {
		return new Promise<boolean>((resolve) => {
			const closeModal = () => {
				setConfirmModalVisible(false);
				setConfirmModalProps(undefined);
			};

			let message = '';
			if (processType === 'inWarehouse') {
				message = '창고 정보 등록 화면에서 제품창고 및 공정창고 권한을 하나이상 등록해주세요';
			} else if (processType === 'outWarehouse') {
				message = '창고 정보 등록 화면에서 자재창고 및 공정창고 권한을 하나이상 등록해주세요';
			} else if (processType === 'badWarehouse') {
				message = '창고 정보 등록 화면에서 불량창고 권한을 하나이상 등록해주세요';
			} else if (processType === 'WMS1030_R01' || processType === 'WMS1031_R01') {
				message = '창고 정보 등록 화면에서 자재창고 및 공정창고 및 제품창고 권한을 하나이상 등록해주세요';
			}

			setConfirmModalProps({
				title: '창고정보 설정 오류',
				message: message,
				confirmText: '확인',
				onConfirm: () => { closeModal(); resolve(true); },
			});
			setConfirmModalVisible(true);
		});
	}, [setConfirmModalProps, setConfirmModalVisible]);

	const warningWorkplaceInfo = useCallback((workplaceNm: string, processType: WarehouseProcessType) => {
		return new Promise<boolean>((resolve) => {
			const closeModal = () => {
				setConfirmModalVisible(false);
				setConfirmModalProps(undefined);
			};

			let message = '';
			if (processType === 'inWarehouse') {
				message = '공정정보 등록 오류가 발생했습니다. 공정명:' + workplaceNm + '의 입고창고를 선택해주세요.';
			} else if (processType === 'outWarehouse') {
				message = '공정정보 등록 오류가 발생했습니다. 공정명:' + workplaceNm + '의 출고창고를 선택해주세요.';
			} else if (processType === 'badWarehouse') {
				message = '공정정보 등록 오류가 발생했습니다. 공정명:' + workplaceNm + '의 불량창고를 선택해주세요.';
			}

			setConfirmModalProps({
				title: '공정정보 설정 오류',
				message: message,
				confirmText: '확인',
				onConfirm: () => { closeModal(); resolve(true); },
			});
			setConfirmModalVisible(true);
		});
	}, [setConfirmModalProps, setConfirmModalVisible]);


	// warningWorkplaceInfo, warningWarehouseInfo, warningProcessInfo을 종합적으로 판단하고 관리하는 검증절차
	// logicCenter, inWarehouseCd, outWarehouseCd, badWarehouseCd을 반환해서 사용가능한 물류정보, 입고창고, 출고창고, 불량창고를 사용할 수 있도록 함
	const validateWorkplaceWarehouse = useCallback(({
		mode = 'workplace',
		workPlaceList = [],
		warehouseCdList,
		wmsCdList,
		workplaceCd = '',
		logicCenter: logicCenterParam,
	}: ValidateWorkplaceWarehouseParams): WorkplaceWarehouseValidateResult => {

		// console.log('validateWorkplaceWarehouse mode :', mode);
		// console.log('validateWorkplaceWarehouse workPlaceList :', workPlaceList);
		// console.log('validateWorkplaceWarehouse warehouseCdList :', warehouseCdList);
		// console.log('validateWorkplaceWarehouse wmsCdList :', wmsCdList);
		// console.log('validateWorkplaceWarehouse workplaceCd :', workplaceCd);
		// console.log('validateWorkplaceWarehouse logicCenterParam :', logicCenterParam);

		//출고등록 화면에서 관리하는 별도의 검증 로직
		//출고등록 화면의 출고창고는 상품장고사용 또는 제품장고사용 권한이 있어야 함
		if (mode === 'WMS1030_R01' || mode === 'WMS1031_R01') {
			const logicCenter = logicCenterParam ?? wmsCdList[0]?.minorCd ?? '';
			const warehouseCdListToUse = warehouseCdList.filter(value => value.logicCenter === logicCenter
				&& (value.goodsYn === 'Y' || value.productYn === 'Y'));

			if (warehouseCdListToUse.length === 0) {
				warningWarehouseInfo(mode);
				return { valid: false };
			}

			return {
				valid: true,
				mode,
				logicCenter,
				warehouseCdListToUse,
			};
		}

		if (workPlaceList.length === 0) {
			warningWorkplaceNullInfo();
			return { valid: false };
		}

		const workPlaceInfo = workPlaceList.find(value => value.workplaceCd === workplaceCd);
		const workplaceNm = workPlaceInfo?.workplaceNm || '';
		const logicCenter = wmsCdList.filter(value => value.minorCd === workPlaceInfo?.logicCenter)[0]?.minorCd ?? '';

		const defaultInWarehouseCd = warehouseCdList.filter(value => value.logicCenter === logicCenter
			&& (value.productYn === 'Y' || value.manufactYn === 'Y'));
		const defaultOutWarehouseCd = warehouseCdList.filter(value => value.logicCenter === logicCenter
			&& (value.materialYn === 'Y' || value.manufactYn === 'Y'));
		const defaultBadWarehouseCd = warehouseCdList.filter(value => value.logicCenter === logicCenter
			&& value.badYn === 'Y');

		if (defaultInWarehouseCd.length === 0) {
			warningWarehouseInfo('inWarehouse');
			return { valid: false };
		}
		if (defaultOutWarehouseCd.length === 0) {
			warningWarehouseInfo('outWarehouse');
			return { valid: false };
		}
		if (defaultBadWarehouseCd.length === 0) {
			warningWarehouseInfo('badWarehouse');
			return { valid: false };
		}

		const inWarehouseCd = defaultInWarehouseCd.filter(value => value.warehouseCd === workPlaceInfo?.inWarehouse);
		const outWarehouseCd = defaultOutWarehouseCd.filter(value => value.warehouseCd === workPlaceInfo?.outWarehouse);
		const badWarehouseCd = defaultBadWarehouseCd.filter(value => value.warehouseCd === workPlaceInfo?.badWarehouse);

		if (inWarehouseCd.length === 0) {
			warningWorkplaceInfo(workplaceNm, 'inWarehouse');
			return { valid: false };
		}
		if (outWarehouseCd.length === 0) {
			warningWorkplaceInfo(workplaceNm, 'outWarehouse');
			return { valid: false };
		}
		if (badWarehouseCd.length === 0) {
			warningWorkplaceInfo(workplaceNm, 'badWarehouse');
			return { valid: false };
		}

		return {
			valid: true,
			mode: 'workplace',
			logicCenter,
			inWarehouseCd,
			outWarehouseCd,
			badWarehouseCd,
		};
	}, [warningWorkplaceNullInfo, warningWorkplaceInfo, warningWarehouseInfo]);

	return {
		warningWorkplaceNullInfo,
		warningWorkplaceInfo,
		warningWarehouseInfo,
		validateWorkplaceWarehouse,
	};
};
